import { entities } from "sbtech-sports-data-api-client";
import { MasterEvent, LiveGameState, Scoreboard } from "../../data";
import { UIPreferences } from "..";
import { getCurrentTime } from "sb-resp-lib";
import { Scores, TeamScoreParser } from "../TeamScoreParser";
import { ScoreboardParser } from "../ScoreboardParser";

type TeamNames = {
    Home: string;
    Away: string;
};

export type FormattedDateTime = {
    Date: string;
    Time: string;
};

export class MasterEventParser {
    private static _instance: MasterEventParser;

    public static get instance(): MasterEventParser {
        if (!MasterEventParser._instance) {
            MasterEventParser._instance = new MasterEventParser();
        }
        return MasterEventParser._instance;
    }

    private constructor() { }

    public parse(sportEvent: entities.SportEvent, uiPreferences: UIPreferences): MasterEvent {
        let teamNames: TeamNames = this.parseTeamNames(sportEvent);
        let teamScores: Scores = TeamScoreParser.instance.parse(sportEvent);
        let dateTime: FormattedDateTime = this.parseDateTime(sportEvent, uiPreferences);
        let liveGameState: LiveGameState | undefined = this.parseLiveGameState(sportEvent);
        let liveStartCountdown: number = this.parseLiveStartCountdown(sportEvent);
        let scoreboard: Scoreboard | undefined = ScoreboardParser.instance.parse(sportEvent);

        let masterEvent: MasterEvent = new MasterEvent({
            eventName: sportEvent.eventName ? sportEvent.eventName : "",
            sportName: sportEvent.sportName,
            sportID: sportEvent.sportId,
            id: sportEvent.id,
            team1Name: teamNames.Home,
            team2Name: teamNames.Away,
            score1: teamScores.Home,
            score2: teamScores.Away,
            leagueID: sportEvent.leagueId,
            leagueName: sportEvent.leagueName,
            eventDate: dateTime.Date,
            eventTime: dateTime.Time,
            isTeamSwapEnabled: sportEvent.isTeamSwap,
            isLive: sportEvent.isLive,
            markets: [],
            eventDateTime: sportEvent.startEventDate,
            liveGameState,
            liveStartCountdown,
            scoreboard,
            regionId: sportEvent.regionId,
            status: sportEvent.status
        });
        return masterEvent;
    }

    private parseLiveGameState(sportEvent: entities.SportEvent): LiveGameState | undefined {
        if (!sportEvent.liveGameState) {
            return undefined;
        }

        return new LiveGameState({
            ...sportEvent.liveGameState,
            gameTime: sportEvent.liveGameState.gameTime ?
                sportEvent.liveGameState.gameTime * 1000 : 0
        });
    }

    public parseLiveStartCountdown(sportEvent: entities.SportEvent): number {
        let eventStartingDate = Date.fromISO(sportEvent.startEventDate);
        if (!eventStartingDate) {
            return 0;
        }

        let delta: number = eventStartingDate.getTime() - (window["serverdate"] || new Date()).getTime();
        return (delta < 0) ? 0 : delta;
    }

    private parseTeamNames(sportEvent: entities.SportEvent): TeamNames {
        if (!sportEvent.participants || !sportEvent.participants[0] || !sportEvent.participants[1]) {
            return { Home: "", Away: "" };
        }

        if (sportEvent.participants[0].venueRole && sportEvent.participants[1].venueRole) {
            return this.parseTeamNamesByVenue(sportEvent);
        }

        return this.parseTeamNamesByIndex(sportEvent);
    }

    private parseTeamNamesByIndex(sportEvent: entities.SportEvent): TeamNames {
        return sportEvent.isTeamSwap
            ? { Home: sportEvent.participants[1].name, Away: sportEvent.participants[0].name }
            : { Home: sportEvent.participants[0].name, Away: sportEvent.participants[1].name };
    }

    private parseTeamNamesByVenue(sportEvent: entities.SportEvent) {
        let homeParticipant = sportEvent.participants.find((par) => par.venueRole === "Home");
        let awayParticipant = sportEvent.participants.find((par) => par.venueRole === "Away");
        return sportEvent.isTeamSwap
            ? { Home: awayParticipant ? awayParticipant.name : "", Away: homeParticipant ? homeParticipant.name : "" }
            : { Home: homeParticipant ? homeParticipant.name : "", Away: awayParticipant ? awayParticipant.name : "" };
    }

    private parseDateTime(sportEvent: entities.SportEvent, uiPreferences: UIPreferences): FormattedDateTime {
        let datetime: FormattedDateTime = this.getFormattedDateTime(sportEvent.startEventDate, uiPreferences.timeZone);

        return {
            Date: datetime.Date,
            Time: datetime.Time
        };
    }

    public getFormattedDateTime(date: string, timeZone: number): FormattedDateTime {
        let convertedDate = Date.fromISO(date);
        if (convertedDate === undefined) {
            throw "invalid argument";
        }

        let currentDate: Date = getCurrentTime(convertedDate, timeZone);
        let formattedDate: string = currentDate.toStringEx("DD, MMM dd");
        let formattedTime: string = currentDate.formatDate("HH:mm");

        return {
            Date: formattedDate,
            Time: formattedTime
        };
    }

    private liveTimeCalculatorByClockDirection = {
        [entities.ClockDirection.Timer]: (gameTime: number, interval: number) => gameTime - interval,
        [entities.ClockDirection.Stopwatch]: (gameTime: number, interval: number) => gameTime + interval
    };

    public getLiveGameStateTime(liveGameState: LiveGameState, newInterval: number): number {
        if (!liveGameState || !liveGameState.gameTime) {
            return 0;
        }

        if (!liveGameState.clockRunning) {
            return liveGameState.gameTime;
        }

        return this.liveTimeCalculatorByClockDirection[liveGameState.clockDirection](liveGameState.gameTime, newInterval);
    }

    public getLiveStartCountdownTime(liveStartCountdown: number, interval: number): number {
        let newLiveStartCountdown: number = liveStartCountdown - interval;
        return newLiveStartCountdown < 0 ? 0 : newLiveStartCountdown;
    }
}